import React, { useEffect, useState, useMemo } from 'react';
import './Users.css';
import { directoryService, projectService } from '../services/api';

const RoleBadge = ({ role }) => {
  const map = {
    admin: { label: 'Admin', color: '#b71c1c' },
    manager: { label: 'Manažér', color: '#0d47a1' },
    supervisor: { label: 'Supervízor', color: '#1b5e20' },
    employee: { label: 'Zamestnanec', color: '#424242' }
  };
  const s = map[role] || map.employee;
  return (
    <span style={{ background: s.color, color: 'white', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
      {s.label}
    </span>
  );
};

const DirectoryCard = ({ user }) => {
  const avatarUrl = user.avatar_url || (user.avatar ? `/api/users/${user.id}/avatar` : null);
  const initials = useMemo(() => {
    const parts = (user.full_name || user.username || '').split(' ').filter(Boolean);
    return parts.slice(0,2).map(p => p[0]?.toUpperCase() || '').join('');
  }, [user.full_name, user.username]);

  return (
    <div className="user-card">
      <div className="user-avatar" style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20 }}>
        {avatarUrl ? (
          <img src={avatarUrl} alt={user.full_name || user.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      <div className="user-info">
        <div className="user-name">{user.full_name || user.username}</div>
        <div className="user-meta">
          <RoleBadge role={user.role} />
        </div>
        {user.phone && (
          <div className="user-contact"><a href={`tel:${user.phone}`}>📞 {user.phone}</a></div>
        )}
        {user.email && (
          <div className="user-contact"><a href={`mailto:${user.email}`}>✉️ {user.email}</a></div>
        )}
      </div>
    </div>
  );
};

export default function Directory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(24);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const all = await projectService.getAll();
        setProjects(all);
      } catch (e) {
        // ignore
      }
    };
    loadProjects();
  }, []);

  const loadData = async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { items, total } = await directoryService.list({ page, page_size: pageSize, q, project_id: projectId || undefined, ...opts });
      setItems(items);
      setTotal(total);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadData({ page: 1 });
  };

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  return (
    <div className="users-page">
      <h2>Adresár kontaktov</h2>

      <form onSubmit={handleSearch} className="filters" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Hľadať meno, email, telefón..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: '1 1 280px', padding: 8 }}
        />
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={{ padding: 8 }}>
          <option value="">Všetky projekty</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button type="submit" className="btn-primary">Hľadať</button>
      </form>

      {loading && <div>Načítavam adresár...</div>}
      {error && <div className="error">Chyba: {error}</div>}

      {!loading && !error && (
        <>
          <div className="users-grid">
            {items.map(u => (
              <DirectoryCard key={u.id} user={u} />
            ))}
          </div>
          <div className="pagination" style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p-1))}>Predchádzajúca</button>
            <span>Strana {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p+1))}>Ďalšia</button>
          </div>
        </>
      )}
    </div>
  );
}
